"use client";

import { useEffect, useMemo, useState } from "react";
import { createClient } from "@/lib/supabase/client";

/**
 * Returns true while any sync run is `running` for the current tenant.
 * Push-based via Supabase Realtime — replaces the previous 5s polling loop
 * that hit /api/erp-connections + /api/erp-connections/[id]/sync on every page.
 *
 * RLS on connector_sync_runs scopes broadcasts to the user's tenant, so no
 * explicit tenant filter is needed in the subscription.
 */
export function useRunningSyncs(enabled: boolean): boolean {
  const [hasRunning, setHasRunning] = useState(false);
  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let lastRefreshAttempt = 0;

    const refresh = async () => {
      const { data } = await supabase
        .from("connector_sync_runs")
        .select("id")
        .eq("status", "running")
        .limit(1);
      if (!cancelled) setHasRunning((data?.length ?? 0) > 0);
    };

    void refresh();

    const channel = supabase
      .channel("connector-sync-runs-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "connector_sync_runs" },
        () => {
          void refresh();
        }
      )
      .subscribe((status, err) => {
        if (err) {
          if (err?.message?.includes("InvalidJWTToken") || err?.message?.includes("Token has expired")) {
            const now = Date.now();
            if (now - lastRefreshAttempt > 30_000) {
              lastRefreshAttempt = now;
              void supabase.auth.refreshSession();
            }
            return;
          }
          const isTransient = err?.message?.includes("1006");
          if (!isTransient) {
            console.warn("connector_sync_runs realtime error:", err);
          }
        }
        // SUBSCRIBED fires on initial connect AND on every auto-reconnect.
        // Re-seeding here closes the "missed event during disconnect" gap.
        if (status === "SUBSCRIBED") void refresh();
      });

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [enabled, supabase]);

  return enabled && hasRunning;
}
